import { Subscription } from 'rxjs';
import {
  Component,
  Directive,
  Input,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ViewContainerRef,
  ComponentFactoryResolver,
  OnInit,
  ComponentRef,
  Optional,
  Output,
  OnDestroy,
  EventEmitter,
} from '@angular/core';
import { AbstractControl, FormArray, FormGroup, NgControl } from '@angular/forms';

import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatFormField } from '@angular/material/form-field';

export interface SgClearField {
  noBubble: boolean;
  clearField: EventEmitter<void>;
}

@Component({
  selector: 'sg-clear-field',
  templateUrl: './clear-field.component.html',
  styleUrls: ['./clear-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SgClearFieldComponent implements SgClearField {
  /**
   * When set to true will block bubbling of the internal click event.
   */
  @Input() noBubble: boolean;

  @Output() clearField = new EventEmitter<void>();

  onClick(event: Event): void {
    if (this.noBubble) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.clearField.emit();
  }
}

function defaultEmptyComparator(value: any): boolean {
  return value === null || value === '';
}

function findIfEmpty(control: AbstractControl, comparator: (value: any) => boolean): boolean {
  // Check if it's a FormGroup by checking for 'controls' property and that controls is an object (not an array)
  if ('controls' in control && !Array.isArray((control as any).controls)) {
    const formGroup = control as FormGroup;
    const keys = Object.keys(formGroup.controls);
    return keys.every((k) => findIfEmpty(formGroup.controls[k], comparator));
  } else if ('controls' in control && Array.isArray((control as any).controls)) {
    // It's a FormArray
    const formArray = control as FormArray;
    return formArray.controls.every((c) => findIfEmpty(c, comparator));
  } else {
    // It's a FormControl or other leaf control
    return comparator(control.value);
  }
}

/**
 * Adds clear field functionality to input controls and mat-form-field.
 * The directive works in 2 modes:
 *   - Control Mode
 *   - Manual Mode
 *
 * ## Control Mode
 * In **Control mode** the directive will use an `NgControl` instance to automatically manage the entire process.
 *
 * In most use cases control mode works automatically, without specific setup. It will get hold of an `NgControl` instance
 * through DI hierarchy (either injected directly or through MatFormField injection).
 *
 * When an `NgControl` instance can not be ontained through DI hierarchy it can be set by the user, using the `controlSource` property binding.
 *
 * In contorl mode the `controlSource` property accepts an instance of `AbstractControl`, `NgControl`, or `MatFormField`.
 *
 * You can apply it on an element with an NgControl instance (input, select, etc) or as content of a MatFormField.
 * The latter is good when you want to set the clear field element as MatSuffix.
 *
 * ### Examples
 *
 * In this example, we apply `sgClearField` to the input element inside the form field (`input`, `select`, etc...).
 * Because we have a form control set (`formControlName="gender"`) we don't need to do anything else, the directive will
 * find the control and manage the entire process.
 *
 * > Setting `sgClearField` on a host with a form control means that `sgClearField` will have direct access to the `NgControl` instance.
 *
 * ```html
 *   <mat-form-field class="form-control">
 *     <mat-select sgClearField formControlName="gender" placeholder="Gender">
 *       <mat-option value="male">Male</mat-option>
 *       <mat-option value="female">Female</mat-option>
 *     </mat-select>
 *   </mat-form-field>
 * ```
 *
 * In this example, we apply `sgClearField` to the an element inside the form field **that is not an input element**.
 * Because `sgClearField` lives inside `mat-form-field` AND the sibling input element has a form control we don't need to do
 * anything else, the directive will find the control and manage the entire process.
 *
 * > `sgClearField` **is NOT** set on a host with a form control but inside `mat-form-field` with a input that has a form control.
 * `sgClearField` will have in-direct access to the `NgControl` instance through the `MatFormField` instance.
 *
 * ```html
 *   <mat-form-field class="form-control">
 *     <mat-select formControlName="gender" placeholder="Gender">
 *       <mat-option value="male">Male</mat-option>
 *       <mat-option value="female">Female</mat-option>
 *     </mat-select>
 *     <ng-container sgClearField matSuffix></ng-container>
 *   </mat-form-field>
 * ```
 *
 * In this example we apply `sgClearField` on an element without a from control.
 * Because we set the control everything will work properly.
 * ```html
 * <div sgClearField [controlSource]="myFormControl">
 *   <input matInput [formConrol]="myFormControl" />
 * </div>
 * ```
 *
 * ## Manual Mode
 * In some scenarios form's are not used and so there is no form control.
 * `sgClearField` can still work but it require manual help.
 *
 * In manual mode `sgClearField` **can not** do the following:
 *   1. Detect when the value change
 *   2. Update the value (clear the value)
 *
 * In maunal mode these tasks are the responsibility of the developer:
 *   1. Manually call `valueChanges` with a new value every time the value changes.
 *   2. Listen to the `fieldClears` event and clear the value every time it emits.
 *
 * To run in manual mode, set the `controlSource` property to the literal string `manual`.
 *
 * ### Examples
 * In this example, we apply `sgClearField` on an input element without a form control, this means need
 * to run it in **manual mode**. Notify `sgClearField` when the value changes and clear the value when notified from `sgClearField`.
 *
 * We create a reference for the input element and for `sgClearField` instance so we can use the API.
 * We opt-in for manual mode (`controlSource="manual"`)
 * We notify `sgClearField` every time the value has changed (`(input)="sgClear.valueChanges($event.target.value)"`)
 * And finaly, when the field clear we make sure to reset it. (`(fieldClears)="input.value = null"`)
 *
 * > Note that `(input)` is an even from the `<input>` element and `(fieldClears)` is an event from the `sgClearField` directive.
 *
 * > This example will also work if we remove `mat-form-field`, it has no effect.
 *
 * ```html
 * <mat-form-field>
 *   <input #input #sgClear="sgClearField"
 *          matInput
 *          sgClearField
 *          controlSource="manual"
 *          (input)="sgClear.valueChanges($event.target.value)"
 *          (fieldClears)="input.value = null" />
 * </mat-form-field>
 * ```
 */
@Directive({
  selector: '[sgClearField]',
  exportAs: 'sgClearField',
  standalone: false,
})
export class SgClearFieldDirective implements OnInit, OnDestroy {
  /**
   * An optional component to render.
   * When not set the default component is used, SgClearFieldComponent.
   */
  @Input() component: any;

  /**
   * When true will block bubbling of the internal click event.
   * This value defaults to true due to the nature of html attributes.
   */
  @Input('sgClearField') get noBubble() {
    return this._noBubble;
  }

  set noBubble(value: BooleanInput) {
    const noBubble = coerceBooleanProperty(value);
    if (noBubble !== this._noBubble) {
      this._noBubble = noBubble;
      if (this.cmpRef) {
        this.cmpRef.instance.noBubble = noBubble;
      }
    }
  }

  /**
   * An optional comparator to check for empty values.
   * When not set a default comparator is used that returns true (empty value) when the value is null or empty string.
   */
  @Input() emptyComparator: (value: any) => boolean;

  @Input() get hideClearField() {
    return this._hide;
  }
  set hideClearField(value: BooleanInput) {
    const hide = coerceBooleanProperty(value);
    if (this._hide !== hide) {
      this._hide = hide;
      this.init();
    }
  }

  /**
   * An optional input that override's the control source for this diretive.
   * When not set, the directive will try to located an NgControl automatically using DI hierarchy.
   *
   * In some cases the element structure requires manual setup of the NgControl.
   * When NgControl is not available you can set this value to 'manual' and the directive will operate in manual mode.
   */
  @Input() set controlSource(value: AbstractControl | NgControl | MatFormField | 'manual') {
    if (value === 'manual') {
      this.manual = true;
    } else if (value instanceof NgControl) {
      this.ngControl = value;
    } else if (value instanceof MatFormField) {
      this.matFormField = value;
    } else {
      this.control = value;
    }
    if (!this.subs.closed) {
      this.init();
    }
  }

  /**
   * Emits when the field has cleard.
   * When running in manual mode (`controlSource = 'manual'`) this event should be used to clear the field.
   */
  @Output() fieldClears = new EventEmitter<void>();

  private cmpRef: ComponentRef<SgClearField>;
  private visible = false;
  private _noBubble = false;
  private subs = Subscription.EMPTY;
  private control: AbstractControl;
  private manual = false;
  private _hide = false;

  constructor(
    private vcRef: ViewContainerRef,
    private cfr: ComponentFactoryResolver,
    @Optional() private ngControl: NgControl,
    @Optional() private matFormField: MatFormField
  ) {
    this.controlSource = this.ngControl || this.matFormField;
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.clearViewRef();
  }

  private init(): void {
    this.subs.unsubscribe();
    if (this._hide) {
      this.clearViewRef();
      return;
    }

    const control =
      this.control ||
      (this.ngControl && this.ngControl.control) ||
      (this.matFormField && this.matFormField._control?.ngControl && this.matFormField._control?.ngControl?.control);

    if (control) {
      this.subs = control.valueChanges.subscribe((value) => this.valueChanges(value, control));
    } else if (!this.manual) {
      throw new Error('sgClearField can be set on a element with a control or as content of MatFormField');
    }
  }

  /**
   * A handler for value change events.
   * This method is used internally when the directive is used within a mat-field / input pair that have an NgControl set. (i.e. not in manual mode)
   *
   * When running in manual mode (`controlSource = 'manual'`) (i.e used without an NgControl) this method should be used to manually trigger the value change handler.
   * @param value The new value
   * @param control An optional control (used when in NON manual mode)
   */
  valueChanges(value: any, control?: AbstractControl): void {
    const comparator: (value: any) => boolean =
      typeof this.emptyComparator === 'function' ? this.emptyComparator : defaultEmptyComparator;
    const isEmpty = control ? control.disabled || findIfEmpty(control, comparator) : comparator(value);

    if (isEmpty) {
      if (this.visible) {
        this.clearViewRef();
      }
    } else {
      if (!this.visible) {
        const cmpFactory = this.cfr.resolveComponentFactory<SgClearField>(this.component || SgClearFieldComponent);
        this.cmpRef = this.vcRef.createComponent<SgClearField>(cmpFactory);

        this.cmpRef.instance.noBubble = this._noBubble;
        const subs = this.cmpRef.instance.clearField.subscribe(() => {
          if (control) {
            control.reset();
          }
          this.fieldClears.emit();
          if (this.manual) {
            this.clearViewRef();
          }
        });
        this.cmpRef.onDestroy(() => subs.unsubscribe());
        this.visible = true;
      }
    }
  }

  private clearViewRef(): void {
    if (this.cmpRef) {
      this.cmpRef.destroy();
      this.cmpRef = undefined;
    }
    this.vcRef.clear();
    this.visible = false;
  }
}
